import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth, hasRole } from "@/lib/auth";
import { APP_VERSION } from "@/lib/version";
import {
  LayoutDashboard, Package, PackagePlus, ArrowDownToLine, ArrowUpFromLine,
  ListTree, AlertTriangle, Building2, BuildingIcon, FileText, FilePlus2,
  ShoppingCart, ShoppingBag, Wallet, CreditCard, Receipt, ClipboardList,
  BarChart3, Tag, Bookmark, Briefcase, Users, Bell, Settings,
  History as HistoryIcon, ClipboardEdit, Wallet as WalletIcon, CalendarDays,
  ChevronDown, ChevronRight, LogOut,
} from "lucide-react";

const Group = ({ title, icon: Icon, items, open, onToggle, currentPath }) => {
  const hasActive = items.some((it) => currentPath.startsWith(it.to));
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`sidebar-group-${title.toLowerCase().replace(/\s+/g, "-")}`}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          hasActive ? "text-white" : "text-white/70 hover:text-white hover:bg-white/5"
        }`}
      >
        <Icon size={18} className="shrink-0" />
        <span className="flex-1 text-left">{title}</span>
        {open || hasActive ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {(open || hasActive) && (
        <div className="ml-2 pl-4 border-l border-white/10 mt-0.5 mb-2">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`sidebar-link-${it.to.replace(/\//g, "-").slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-[13px] transition-colors ${
                  isActive
                    ? "text-white bg-white/10 font-semibold border-l-2 border-[#FFE600] -ml-[2px] pl-[14px]"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const SoloLink = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-1 ${
        isActive
          ? "bg-white/10 text-white border-l-4 border-[#FFE600] -ml-[4px] pl-[8px]"
          : "text-white/70 hover:text-white hover:bg-white/5"
      }`
    }
  >
    <Icon size={18} />
    <span>{label}</span>
  </NavLink>
);

export default function Sidebar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [openGroup, setOpenGroup] = useState(null);
  const toggle = (g) => setOpenGroup(openGroup === g ? null : g);

  const canSee = (...roles) => hasRole(user, ...roles);

  return (
    <aside
      data-testid="sidebar"
      className="hidden lg:flex w-64 bg-[#08285A] text-white h-screen sticky top-0 flex-col"
    >
      <div className="px-4 py-4 border-b border-white/10 flex justify-center">
        <div className="bg-white rounded-lg px-3 py-2 w-full">
          <img src="/azd-logo.png" alt="AZD Ambalaj"
               className="h-9 w-auto mx-auto" />
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto azd-scrollbar">
        {canSee("admin") && (
          <SoloLink to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" />
        )}

        {canSee("admin", "sales", "accounting", "warehouse", "procurement") && (
          <Group
            title="Ürün & Stok"
            icon={Package}
            open={openGroup === "stock"}
            onToggle={() => toggle("stock")}
            currentPath={loc.pathname}
            items={[
              { to: "/products", label: "Ürünler" },
              ...(canSee("admin", "warehouse")
                ? [
                    { to: "/products/new", label: "Ürün Ekle" },
                    { to: "/stock/entry", label: "Stok Girişi" },
                    { to: "/stock/decrease", label: "Stok Düş" },
                  ]
                : []),
              { to: "/stock/movements", label: "Stok Hareketleri" },
              { to: "/stock/critical", label: "Kritik Stoklar" },
              ...(canSee("admin", "warehouse")
                ? [{ to: "/productions", label: "Üretim" }]
                : []),
              ...(canSee("admin", "warehouse", "accounting", "procurement")
                ? [
                    { to: "/purchase-orders/new", label: "Satın Alma Siparişi Ekle" },
                    { to: "/purchase-orders", label: "Satın Alma Siparişleri" },
                  ]
                : []),
            ]}
          />
        )}

        {canSee("admin", "sales", "accounting", "procurement") && (
          <Group
            title="Firmalar"
            icon={Building2}
            open={openGroup === "companies"}
            onToggle={() => toggle("companies")}
            currentPath={loc.pathname}
            items={[
              { to: "/companies", label: "Firma Listesi" },
              ...(canSee("admin", "sales") ? [{ to: "/companies/new", label: "Firma Ekle" }] : []),
            ]}
          />
        )}

        {canSee("admin", "sales") && (
          <Group
            title="Satış"
            icon={ShoppingCart}
            open={openGroup === "sales"}
            onToggle={() => toggle("sales")}
            currentPath={loc.pathname}
            items={[
              { to: "/quotes/new", label: "Teklif Hazırla" },
              { to: "/quotes", label: "Tekliflerim" },
              { to: "/orders/new", label: "Sipariş Hazırla" },
              { to: "/orders", label: "Siparişler" },
              { to: "/ecommerce/sales/new", label: "E-Ticaret Satışı Ekle" },
              { to: "/ecommerce/sales", label: "E-Ticaret Satışları" },
            ]}
          />
        )}

        {canSee("admin", "accounting") && (
          <Group
            title="Cari"
            icon={Wallet}
            open={openGroup === "cari"}
            onToggle={() => toggle("cari")}
            currentPath={loc.pathname}
            items={[
              { to: "/cari", label: "Cari Takip" },
              { to: "/payments/new", label: "Ödeme Girişi" },
              { to: "/cari/movements", label: "Cari Hareketler" },
              { to: "/cari/aging", label: "Yaşlandırma Raporu" },
            ]}
          />
        )}

        {canSee("admin", "sales", "accounting", "viewer") && (
          <Group
            title="Raporlar"
            icon={BarChart3}
            open={openGroup === "reports"}
            onToggle={() => toggle("reports")}
            currentPath={loc.pathname}
            items={[
              { to: "/reports/stock", label: "Stok Raporları" },
              { to: "/reports/sales", label: "Satış Raporları" },
              ...(canSee("admin", "accounting", "viewer")
                ? [{ to: "/reports/cari", label: "Cari Raporları" }]
                : []),
              ...(canSee("admin")
                ? [{ to: "/reports/sales-companies", label: "Satış Personeli Firma Raporu" }]
                : []),
            ]}
          />
        )}

        {canSee("admin") && (
          <Group
            title="Tanımlar"
            icon={Tag}
            open={openGroup === "defs"}
            onToggle={() => toggle("defs")}
            currentPath={loc.pathname}
            items={[
              { to: "/definitions/categories", label: "Kategoriler" },
              { to: "/definitions/brands", label: "Markalar" },
              { to: "/definitions/service-areas", label: "Hizmet Alanları" },
              { to: "/definitions/marketplaces", label: "E-Ticaret Pazaryerleri" },
            ]}
          />
        )}

        {canSee("admin", "accounting") && (
          <SoloLink to="/personnel" icon={Users} label="Personeller" testid="nav-personnel" />
        )}

        {canSee("admin", "accounting") && (
          <SoloLink to="/assignments" icon={Package} label="Personel Zimmet Listesi" testid="nav-assignments" />
        )}

        {canSee("admin") && (
          <SoloLink to="/audit-logs" icon={HistoryIcon} label="Audit Log" testid="nav-audit" />
        )}

        <SoloLink to="/activity-reports" icon={ClipboardEdit}
                   label={canSee("admin") ? "Faaliyet Raporları" : "Faaliyet Raporum"}
                   testid="nav-activity-reports" />

        <SoloLink to="/expenses" icon={WalletIcon} label="Giderler" testid="nav-expenses" />

        <SoloLink to="/work-plans" icon={CalendarDays}
                   label={canSee("admin") ? "İş Planları" : "Haftalık İş Planım"}
                   testid="nav-work-plans" />

        <SoloLink to="/notifications" icon={Bell} label="Bildirimler" testid="nav-notifications" />
      </nav>

      <div className="px-3 py-3 border-t border-white/10">
        <button
          onClick={logout}
          data-testid="sidebar-logout"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition-colors"
        >
          <LogOut size={18} />
          <span>Çıkış Yap</span>
        </button>
        <div
          className="mt-2 px-3 py-1 text-center text-[10px] text-white/40 font-mono tracking-wider"
          data-testid="app-version"
        >
          Versiyon {APP_VERSION}
        </div>
      </div>
    </aside>
  );
}
